import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

const BUILD_ID_STORAGE_KEY = 'sherlook-provider:buildId';
const BUILD_INFO_URL = `${process.env.PUBLIC_URL || ''}/build-info.json`;
/** Vérification périodique tant que l’onglet est ouvert. */
const UPDATE_POLL_INTERVAL_MS = 2 * 60 * 1000;

type BuildInfo = {
  buildId?: string;
};

/**
 * Détecte un nouveau déploiement (build-info.json) et propose un rechargement.
 */
export default function AppBuildWatcher() {
  const [updateReady, setUpdateReady] = useState(false);
  const [pendingBuildId, setPendingBuildId] = useState<string | null>(null);

  const applyBuildId = useCallback((buildId: string) => {
    try {
      sessionStorage.setItem(BUILD_ID_STORAGE_KEY, buildId);
    } catch {
      /* sessionStorage indisponible */
    }
  }, []);

  const reloadForUpdate = useCallback(
    (buildId: string) => {
      applyBuildId(buildId);
      window.location.reload();
    },
    [applyBuildId]
  );

  const checkForUpdate = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') return;

    try {
      const response = await fetch(`${BUILD_INFO_URL}?v=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const info = (await response.json()) as BuildInfo;
      const remoteBuildId = info.buildId?.trim();
      if (!remoteBuildId || remoteBuildId === 'development') return;

      let storedBuildId: string | null = null;
      try {
        storedBuildId = sessionStorage.getItem(BUILD_ID_STORAGE_KEY);
      } catch {
        storedBuildId = null;
      }

      if (!storedBuildId) {
        applyBuildId(remoteBuildId);
        return;
      }

      if (storedBuildId === remoteBuildId) return;

      setPendingBuildId(remoteBuildId);
      setUpdateReady(true);
    } catch {
      /* réseau / fichier absent */
    }
  }, [applyBuildId]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    }, UPDATE_POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [checkForUpdate]);

  const handleReload = () => {
    if (pendingBuildId) {
      reloadForUpdate(pendingBuildId);
      return;
    }
    void checkForUpdate().finally(() => window.location.reload());
  };

  return (
    <Snackbar
      open={updateReady}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 88, sm: 24 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setUpdateReady(false)}
        action={
          <Button color="inherit" size="small" onClick={handleReload} sx={{ fontWeight: 700 }}>
            Actualiser
          </Button>
        }
        sx={{ width: '100%', alignItems: 'center' }}
      >
        Une nouvelle version du dashboard est disponible.
      </Alert>
    </Snackbar>
  );
}
